import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { formatCents, type AssetRegister } from '@kontor/shared';
import { api, queryString } from '../../api';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { useSession } from '../../session';
import { formatDay } from './shared';
import './inventory.css';

const endOfLastYear = () => `${new Date().getFullYear() - 1}-12-31`;
const today = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin' }).format(new Date());

/**
 * Anlagenspiegel (KON-166): Inventar und Fahrzeuge mit Anschaffung, Abschreibung und Restwert zum Stichtag, mit Summen und CSV-Export für den Steuerberater. Die Abschreibung
 * ist linear, monatsgenau ab dem Anschaffungsmonat, vom Nettobetrag.
 */
export function AssetRegisterPage() {
  const { areaHidden } = useSession();
  const [asOf, setAsOf] = useState(today());
  const [retired, setRetired] = useState(false);
  const params = { asOf, retired: retired ? '1' : '' };
  const data = useQuery({ queryKey: ['inventory-asset-register', params], queryFn: async () => (await api<{ data: AssetRegister }>('GET', `/inventory/asset-register${queryString(params)}`)).data });
  const r = data.data;
  const link = (row: AssetRegister['rows'][number]) => (row.source === 'vehicle' ? `/flotte/${row.id}` : `/inventar/${row.id}`);

  return (
    <>
      <div className="page-head"><h1>Anlagenspiegel</h1></div>
      <p className="muted">{!areaHidden('/inventar') && <><Link to="/inventar">← Inventar</Link> · </>}Alle Anlagegüter mit Anschaffung, Abschreibung und Restwert zu einem Stichtag.</p>
      <div className="card inv-booking">
        <div className="form-grid">
          <label className="field"><span>Stichtag</span><input className="input" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value || today())} /></label>
          <div className="field">
            <span className="field__label">Schnellwahl</span>
            <div className="inv-actions">
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => setAsOf(today())}>Heute</button>
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => setAsOf(endOfLastYear())}>Ende letztes Jahr</button>
            </div>
          </div>
          <label className="field inv-check"><span><input type="checkbox" checked={retired} onChange={(e) => setRetired(e.target.checked)} /> Ausgeschiedene Stücke mit aufführen</span></label>
        </div>
        <p className="small"><a href={`/api/v1/inventory/asset-register/export${queryString(params)}`} className="strong-link">Als CSV exportieren</a> <span className="muted">(für den Steuerberater, mit Summenzeile)</span></p>
      </div>

      {data.isError && <div className="alert alert--error">{(data.error as Error).message}</div>}
      {r && (
        <>
          <div className="inv-tiles">
            <div className="card inv-tile"><span className="muted small">Anlagegüter</span><strong>{r.totals.count}</strong>{r.incomplete > 0 && <span className="inv-warn small">{r.incomplete} ohne Angaben</span>}</div>
            <div className="card inv-tile"><span className="muted small">Anschaffung netto</span><strong>{formatCents(r.totals.netCents)}</strong></div>
            <div className="card inv-tile"><span className="muted small">Abgeschrieben</span><strong>{formatCents(r.totals.depreciatedCents)}</strong></div>
            <div className="card inv-tile"><span className="muted small">Restwert zum {formatDay(r.asOf)}</span><strong>{formatCents(r.totals.bookValueCents)}</strong></div>
          </div>
          <div className="card table-card">
            <table className="table table--cards">
              <thead>
                <tr><th>Nummer</th><th>Bezeichnung</th><th>Angeschafft</th><th className="num">Netto</th><th className="num">Nutzung</th><th className="num">Abgeschrieben</th><th className="num">Restwert</th></tr>
              </thead>
              <tbody>
                {data.isLoading && <tr><td colSpan={8} className="muted">Wird geladen …</td></tr>}
                {r.rows.length === 0 && <tr><td colSpan={8} className="empty">Keine Anlagegüter zu diesem Stichtag.</td></tr>}
                {r.rows.map((x) => (
                  <ExpandRow
                    key={`${x.source}-${x.id}`}
                    className={x.ended ? 'inv-reversed' : ''}
                    summary={<Summary title={<Link to={link(x)} className="strong-link">{x.name}</Link>} aside={<strong>{x.bookValueCents !== null ? formatCents(x.bookValueCents) : '–'}</strong>} line2={`${x.number}${x.category ? ` · ${x.category}` : ''}`} line3={x.acquiredOn ? `angeschafft ${formatDay(x.acquiredOn)}` : 'ohne Anschaffungsdatum'} />}
                  >
                    <td className="cell-desktop-title"><Link to={link(x)} className="strong-link">{x.number}</Link>{x.ended && <div className="muted small">ausgeschieden</div>}</td>
                    <td data-label="Bezeichnung">{x.name}{x.category && <div className="muted small">{x.category}</div>}</td>
                    <td data-label="Angeschafft">{formatDay(x.acquiredOn)}</td>
                    <td data-label="Netto" className="num">{x.netCents !== null ? formatCents(x.netCents) : <span className="inv-warn">fehlt</span>}</td>
                    <td data-label="Nutzung" className="num">{x.depreciationYears ? `${x.depreciationYears} J.` : '–'}</td>
                    <td data-label="Abgeschrieben" className="num">{x.depreciatedCents !== null ? formatCents(x.depreciatedCents) : '–'}</td>
                    <td data-label="Restwert" className="num"><strong>{x.bookValueCents !== null ? formatCents(x.bookValueCents) : '–'}</strong></td>
                  </ExpandRow>
                ))}
              </tbody>
              {r.rows.length > 0 && (
                <tfoot>
                  <tr className="inv-total"><td colSpan={3}><strong>Summe</strong></td><td className="num"><strong>{formatCents(r.totals.netCents)}</strong></td><td /><td className="num"><strong>{formatCents(r.totals.depreciatedCents)}</strong></td><td className="num"><strong>{formatCents(r.totals.bookValueCents)}</strong></td></tr>
                </tfoot>
              )}
            </table>
          </div>
          {r.incomplete > 0 && <p className="muted small">Stücke ohne Anschaffungskosten, Steuersatz, Anschaffungsdatum oder Nutzungsdauer stehen in der Liste, fehlen aber in den Summen. Ergänzen Sie die Angaben am jeweiligen Stück.</p>}
        </>
      )}
    </>
  );
}
